import { useCallback, useMemo, useState } from 'react'

import type { NewDropdownValue, NewDropdownValueArray, UseDropdownValueProps } from '../types'

export function useDropdownValue<T = NewDropdownValue | NewDropdownValueArray>(
  props: UseDropdownValueProps<T>,
) {
  const isControlled = props.value !== undefined
  const isMultiSelect = props.selectionMode === 'multiple'
  const allowUnselectInSingleMode = props.allowUnselectInSingleMode ?? false

  const [internalValue, setInternalValue] = useState<T>(
    props.defaultValue ?? (isMultiSelect ? ([] as T) : ('' as T)),
  )

  // Use controlled or uncontrolled value
  const currentValue = isControlled ? props.value : internalValue

  // Memoize setValue to prevent unnecessary re-renders
  const setValue = useCallback(
    (newValue: T) => {
      if (!isControlled) {
        setInternalValue(newValue)
      }
      props.onChange?.(newValue)
    },
    [isControlled, props],
  )

  // Memoize selected options to avoid recalculation
  const selectedOptions = useMemo(() => {
    if (isMultiSelect) {
      return (currentValue as NewDropdownValueArray) || []
    } else {
      return currentValue ? [currentValue as NewDropdownValue] : []
    }
  }, [currentValue, isMultiSelect])

  // Memoize selected options set for faster lookups
  const selectedOptionsSet = useMemo(() => new Set(selectedOptions), [selectedOptions])

  // Memoize clear handler
  const handleClear = useCallback(
    (onResetViewMode?: () => void) => {
      const defaultValue = isMultiSelect ? [] : ''
      setValue(defaultValue as T)
      // Reset view mode to show all options
      onResetViewMode?.()
    },
    [isMultiSelect, setValue],
  )

  const handleSelectAll = useCallback(
    (allValues: NewDropdownValueArray, onResetViewMode?: () => void) => {
      if (isMultiSelect) {
        setValue(allValues as T)
        onResetViewMode?.()
      }
    },
    [isMultiSelect, setValue],
  )

  // Memoize option selection handler
  const handleOptionSelect = useCallback(
    (
      optionValue: NewDropdownValue,
      setIsOpen?: (value: boolean) => void,
      setSearch?: (value: string) => void,
      searchable?: boolean,
    ) => {
      if (isMultiSelect) {
        const currentArray = (currentValue as NewDropdownValueArray) || []
        const newArray = currentArray.includes(optionValue)
          ? currentArray.filter((v) => v !== optionValue)
          : [...currentArray, optionValue]
        setValue(newArray as T)
      } else {
        // In single select mode
        const isCurrentlySelected = (currentValue as NewDropdownValue) === optionValue

        // If clicking the selected option and unselect is not allowed, do nothing
        if (isCurrentlySelected && !allowUnselectInSingleMode) {
          return
        }

        const newValue = isCurrentlySelected ? '' : optionValue
        setValue(newValue as T)
        setIsOpen?.(false)
        if (searchable) {
          setSearch?.('')
        }
      }
    },
    [isMultiSelect, currentValue, setValue, allowUnselectInSingleMode],
  )

  return {
    // State
    currentValue,
    selectedOptions,
    selectedOptionsSet,
    isMultiSelect,

    // Handlers
    setValue,
    handleClear,
    handleSelectAll,
    handleOptionSelect,
  }
}
